import { ITEM_AVAILABILITY_STATUS } from 'features/Sku/Items/SkuItems.constants';
import { isVisibleSkuEntity } from 'features/Sku/Items/SkuMenuData.normalizers';

import { compileRulesToGroupFields, defaultRulesFormValues, rulesFormValuesFromGroup } from './RulesBlock';
import { getModifierAvailabilityStatus, toModifierAvailability } from './Modifiers.availability';

const CLIENT_OPTION_ID_PREFIX = 'new-option-';

let optionSequence = 0;

const nextClientOptionId = () => {
  optionSequence += 1;
  return `${CLIENT_OPTION_ID_PREFIX}${optionSequence}`;
};

export const isUnsavedOptionId = id => typeof id === 'string' && id.startsWith(CLIENT_OPTION_ID_PREFIX);

export const blankOption = () => ({
  core: { id: nextClientOptionId(), name: '', description: '' },
  availabilityStatus: ITEM_AVAILABILITY_STATUS.AVAILABLE,
});

export const visibleModifiers = group => (group?.modifiers || []).filter(isVisibleSkuEntity);

export const toFormValues = loadedGroup => {
  const modifiers = visibleModifiers(loadedGroup).map(modifier => ({
    core: { ...modifier.core },
    availabilityStatus: getModifierAvailabilityStatus(modifier),
  }));

  return {
    availabilityStatus: getModifierAvailabilityStatus(loadedGroup),
    name: loadedGroup?.core?.name || '',
    itemIds: (loadedGroup?.attachedItems || []).map(item => item.itemId),
    modifiers: modifiers.length > 0 ? modifiers : [blankOption()],
    rules: loadedGroup ? rulesFormValuesFromGroup(loadedGroup, modifiers.length) : defaultRulesFormValues(),
  };
};

// The duplicate carries the source's options and rules into a fresh form: new client ids, every
// option available (a created modifier is always enabled), and the default remapped to the copied
// option's new id so the rules block still points at a row that exists.
export const toDuplicateSeed = (group, copyName) => {
  const base = toFormValues(group);
  const modifiers = base.modifiers.map(modifier => {
    const fresh = blankOption();
    return { ...fresh, core: { ...fresh.core, name: modifier.core.name, description: modifier.core.description } };
  });
  const defaultIndex = base.modifiers.findIndex(modifier => modifier.core.id === base.rules.defaultOptionId);

  return {
    availabilityStatus: ITEM_AVAILABILITY_STATUS.AVAILABLE,
    name: copyName,
    itemIds: [],
    modifiers,
    rules: {
      ...base.rules,
      defaultOptionId: defaultIndex >= 0 ? modifiers[defaultIndex].core.id : '',
      hasDefault: defaultIndex >= 0,
    },
  };
};

export const namedOptions = values => (values.modifiers || []).filter(modifier => (modifier.core?.name || '').trim());

export const toOptionInput = (modifier, index, rules) => ({
  name: (modifier.core?.name || '').trim(),
  description: modifier.core?.description || '',
  ordinal: index + 1,
  isDefault: Boolean(rules?.hasDefault) && modifier.core?.id === rules?.defaultOptionId,
  ...toModifierAvailability(modifier.availabilityStatus),
});

export const toGroupFields = values => ({
  enabled: values.availabilityStatus !== ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY,
  name: (values.name || '').trim(),
  ...compileRulesToGroupFields(values.rules, namedOptions(values).length),
});

export const toGroupPayload = values => ({
  ...toGroupFields(values),
  modifiers: namedOptions(values).map((modifier, index) => toOptionInput(modifier, index, values.rules)),
});

export const toOptionViews = values =>
  (values.modifiers || []).map((modifier, index) => ({
    id: modifier.core?.id || `option-${index}`,
    ...toOptionInput(modifier, index, values.rules),
  }));
